<template>
  <div>
    <img v-if="printMode" class="ui fluid image" :src="image" :title="label"/>
    <div v-else ref="widget"></div>
  </div>
</template>

<script>
import _ from 'lodash';

export default {
  props: {
    title: {
      type: String
    },
    type: {
      type: String
    },
    category: {
      type: Object
    },
    series: {
      type: Array
    },
    label: {
      type: Object
    },
    dataSource: {
      type: Array
    },
    axis: {
      type: Object
    },
    format: {
      type: String,
      default: '{0:n2}'
    },
    height: {
      type: Number,
      default: 350
    },
    colors: {
      type: Array,
      default: () => ['#1ABC9C', '#3498DB', '#9B59B6', '#34495E', '#E74C3C', '#E67E22', '#F1C40F', '#ECF0F1']
    },
    printMode: {
      type: Boolean
    }
  },

  data () {
    let seriesLabel = {};
    if (this.label) {
      seriesLabel = this.label;
      seriesLabel.font = 'bold 11px "Roboto Condensed"';
      seriesLabel.format = this.format;
      seriesLabel.rotation = this.dataSource.length <= 12 ? 0 : 270;
    }

    const config = {
      theme: 'bootstrap',
      title: {
        visible: !!this.title,
        text: this.title
      },
      chartArea: {
        height: this.height,
        background: 'transparent',
        margin: {
          top: 10,
          left: 20,
          bottom: 20,
          right: 20
        }
      },
      legend: {
        position: 'top'
      },
      seriesDefaults: {
        type: 'bubble',
        labels: seriesLabel || false,
        tooltip: {
          visible: true,
          format: this.format
        }
      },
      series: this.series,
      seriesColors: this.colors,
      xAxis: {
        labels: {
          format: this.axis.x.format || '{0}'
        },
        title: {
          text: this.axis.x.title,
          position: 'left',
          font: 'bold 11px "Roboto Condensed"'
        },
        axisCrossingValue: this.axis.measure.x.getValue(),
        line: {
          color: '#E74C3C',
          width: 2
        },
        min: this.axis.measure.x.measure('min') - this.axis.pad.x,
        max: this.axis.measure.x.measure('max') + this.axis.pad.x
      },
      yAxis: {
        labels: {
          format: this.axis.y.format || '{0}'
        },
        title: {
          text: this.axis.y.title,
          position: 'left',
          font: 'bold 11px "Roboto Condensed"'
        },
        axisCrossingValue: this.axis.measure.y.getValue(),
        line: {
          color: '#E74C3C',
          width: 2
        },
        min: this.axis.measure.y.measure('min') - this.axis.pad.y,
        max: this.axis.measure.y.measure('max') + this.axis.pad.y
      },
      zoomable: true,
      pannable: true,
      valueAxis: {
        majorGridLines: {
          visible: false
        },
        labels: {
          font: 'bold 11px "Roboto Condensed"',
          format: this.format
        }
      },
      categoryAxis: {
        field: this.category.field,
        majorGridLines: {
          visible: false
        },
        labels: {
          visible: true,
          rotation: 'auto',
          font: 'bold 11px "Roboto Condensed"'
        }
      },
      tooltip: {
        visible: true,
        template: `<p align="left">
                      <b>${this.axis.x.title}: </b>#= kendo.format("${this.axis.x.format || '{0}'}", value.x) #<br/>
                      <b>${this.axis.y.title}: </b>#= kendo.format("${this.axis.y.format || '{0}'}", value.y) #<br/>
                      <b>${this.axis.size.title}:</b>#= kendo.format("${this.axis.size.format || '{0}'}", value.size) #<br/>
                      <b>${this.category.title}: </b>#= category  #
                  </p>`,
        background: '#fff',
        color: '#303030',
        border: {
          color: '#bdbdbd'
        }
      }
    };

    if (this.dataSource) {
      config.dataSource = {
        data: this.dataSource
      };
    }

    return {
      config,
      image: undefined,
      seriesLabel,
      widget: undefined
    };
  },

  methods: {
    exportChart () {
      this.widget.exportImage().done((data) => {
        kendo.saveAs({
          dataURI: data,
          fileName: 'chart.png'
        });
      });
    },

    generateImage () {
      this.widget.exportImage().done((image) => {
        this.image = image;
      });
    },

    refreshChart () {
      this.widget.refresh();
    }
  },

  mounted () {
    this.widget = $(this.$refs.widget).kendoChart(this.config).data('kendoChart');
    window.addEventListener('resize', this.refreshChart);
  },

  beforeDestroy () {
    window.removeEventListener('resize', this.refreshChart);
  },

  destroyed () {
    this.widget.destroy();
  },

  watch: {
    printMode () {
      this.generateImage();
    },

    dataSource (val) {
      if (this.label) {
        const seriesLabel = this.label;
        seriesLabel.font = 'bold 11px "Roboto Condensed"';
        seriesLabel.format = this.format;
        seriesLabel.rotation = val.length <= 12 ? 0 : 270;
        this.config.seriesDefaults.labels = seriesLabel;
        this.widget.setOptions(this.config);
      }

      this.widget.setDataSource(new kendo.data.DataSource({ data: val }));
    },

    category (newVal, oldVal) {
      if (!_.isEqual(newVal, oldVal)) {
        const categoryAxis = Object.assign({}, this.config.categoryAxis, {
          field: newVal.field
        });
        this.widget.setOptions({ categoryAxis });
      }
    },

    series (newVal, oldVal) {
      if (!_.isEqual(newVal, oldVal)) {
        this.widget.setOptions({ series: newVal });
      }
    }
  }
};
</script>

<style lang="scss" scoped>
</style>
